import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import api from '@/services/api';
import { ReleaseNoteItem } from '@/types';

const productSuiteImages: Record<string, string> = {
  'Fresh 360': '/images/green.png',
  'Compliance 360': '/images/red.png',
  'Waste 360': '/images/orange.png',
  'Commerce 360': '/images/purple.png',
  'Inventory 360': '/images/blue.png',
  'General': '/images/grey.png',
};

const getProductSuiteImage = (suite: string) => {
  return productSuiteImages[suite] || productSuiteImages['General'];
};

export default function EditRelease() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [releaseDate, setReleaseDate] = useState<Date | null>(null);
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Fetch existing release
  const { data: release, isLoading: releaseLoading } = useQuery({
    queryKey: ['release', id],
    queryFn: () => api.getRelease(id!),
    enabled: !!id,
  });

  // Fetch available items (both published in this release and unpublished items)
  const { data: availableItemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['releaseNoteItems', 'forEdit'],
    queryFn: () => api.getReleaseNoteItems({
      excludePublished: false, // Include published items
      excludeExcluded: true,
    }),
  });

  // Initialize form with existing release data
  useEffect(() => {
    if (release) {
      setTitle(release.title);
      setReleaseDate(new Date(release.releaseDate));
      setDescription(release.description || '');
      setVersion(release.version || '');
      setSelectedIds(new Set(release.itemIds || []));
    }
  }, [release]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: () => api.updateRelease(id!, {
      title,
      releaseDate: releaseDate?.toISOString() || new Date().toISOString(),
      description,
      version: version || undefined,
      itemIds: Array.from(selectedIds),
    }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release', id] });
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItems'] });
      navigate('/history', { 
        state: { 
          message: `Release updated successfully! ${result.itemsAdded} items added, ${result.itemsRemoved} items removed.` 
        } 
      });
    },
  });

  // Filter items: items in this release OR unpublished items available to add
  const allItems = availableItemsData?.items || [];
  const releaseItemIds = new Set(release?.itemIds || []);
  
  // Items available to add (not published, or published to this release)
  const availableToAdd = allItems.filter(item => 
    !item.releaseId || item.releaseId === id
  );

  const selectedItems = allItems.filter(item => selectedIds.has(item.id));

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedIds(newSelected);
  };

  // Group items by product suite and module for preview
  const groupedItems = selectedItems.reduce((acc, item) => {
    const suite = item.productSuite || 'Unmapped';
    const mod = item.module || 'General';
    if (!acc[suite]) acc[suite] = {};
    if (!acc[suite][mod]) acc[suite][mod] = [];
    acc[suite][mod].push(item);
    return acc;
  }, {} as Record<string, Record<string, ReleaseNoteItem[]>>);

  if (releaseLoading || itemsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!release) {
    return <Alert severity="error">Release not found</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Edit Release
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Editing: {release.title}
      </Typography>

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error updating release: {(updateMutation.error as Error)?.message}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Release Details" />
        <Tab label={`Manage Items (${selectedIds.size})`} />
        <Tab label="Preview" />
      </Tabs>

      {/* Tab 0: Release Details */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Release Date"
                value={releaseDate}
                onChange={(newValue) => setReleaseDate(newValue)}
              />
            </LocalizationProvider>

            <TextField
              label="Version (optional)"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.2.3"
            />

            <TextField
              label="Description"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
        </Paper>
      )}

      {/* Tab 1: Manage Items */}
      {activeTab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Check items to include in the release. Unchecking an item will remove it from the release 
            and make it available for future releases.
          </Alert>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Include</TableCell>
                  <TableCell>Jira Key</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>Product Suite</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableToAdd.map((item) => {
                  const isInRelease = releaseItemIds.has(item.id);
                  const isSelected = selectedIds.has(item.id);
                  
                  return (
                    <TableRow 
                      key={item.id}
                      sx={{ 
                        backgroundColor: isInRelease && !isSelected 
                          ? 'error.light' 
                          : !isInRelease && isSelected 
                            ? 'success.light' 
                            : 'inherit',
                        opacity: isInRelease && !isSelected ? 0.7 : 1,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>{item.issueKey}</TableCell>
                      <TableCell>{item.summary}</TableCell>
                      <TableCell>{item.productSuite || '-'}</TableCell>
                      <TableCell>
                        {isInRelease && isSelected && (
                          <Chip label="In Release" size="small" color="primary" />
                        )}
                        {isInRelease && !isSelected && (
                          <Chip label="Will Remove" size="small" color="error" />
                        )}
                        {!isInRelease && isSelected && (
                          <Chip label="Will Add" size="small" color="success" />
                        )}
                        {!isInRelease && !isSelected && (
                          <Chip label="Available" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab 2: Preview */}
      {activeTab === 2 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {releaseDate && format(releaseDate, 'MMMM dd, yyyy')}
            </Typography>
            {version && (
              <Typography variant="body2" color="text.secondary">
                Version: {version}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
              {description}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Items ({selectedIds.size})
          </Typography>

          {Object.entries(groupedItems).map(([suite, modules]) => (
            <Paper key={suite} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <img
                  src={getProductSuiteImage(suite)}
                  alt={suite}
                  style={{ width: 32, height: 32 }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  {suite}
                </Typography>
              </Box>
              {Object.entries(modules).map(([mod, modItems]) => (
                <Box key={mod} sx={{ mb: 2, ml: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {mod}
                  </Typography>
                  {modItems.map((item) => (
                    <Box key={item.id} sx={{ mb: 1, ml: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.issueKey} - {item.summary}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {item.currentContent || item.generatedContent}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={() => navigate('/history')}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => updateMutation.mutate()}
          disabled={selectedIds.size === 0 || !title || !releaseDate || updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update Release'}
        </Button>
      </Box>
    </Box>
  );
}
